/*
 * The documents a school or a family attached to a referral (TDI-602).
 *
 * Mirror of python-functions/constants/rms_referral_uploads.py, kept in step
 * by a parity test. The server resolves every label before a row reaches a
 * screen, so the overview card, the Documents panel and the admin handover
 * cannot drift apart; these are here for the screens that build a row
 * themselves and for the parity check.
 */

export const DOCUMENT_TYPES = {
  // Education
  sen_support_plan: 'SEN Support Plan or Assess, Plan, Do, Review records',
  individual_education_plan: 'Individual Education Plan',
  ehcp: 'EHCP and assessment paperwork',
  educational_psychology: 'Educational Psychology report',
  learning_assessment: 'Learning assessments or attainment records',
  attendance: 'Attendance records',
  adjustments_interventions: 'Records of adjustments, interventions and outcomes',
  school_report: 'End-of-year school report',
  // Health and development
  paediatric: 'Paediatric report',
  clinical_psychology: 'Clinical psychology report',
  gp_summary: 'GP summary care record',
  speech_and_language: 'Speech and language therapy',
  occupational_therapy: 'Occupational therapy',
  other_therapy: 'Physiotherapy or other therapy',
  developmental_assessment: 'Developmental assessment',
  hearing_or_vision: 'Hearing or vision assessment',
  mental_health: 'Mental health or CAMHS report',
  // Previous assessments
  previous_autism_or_adhd: 'Previous autism or ADHD assessment report',
  other_neurodevelopmental: 'Report on another neurodevelopmental condition',
  // Social care
  social_care_plan: 'Child in Need, Child Protection or Looked After Child documentation',
  early_help: 'CAF, Team Around the Family or Early Help meeting report',
  other: 'Other',
};

export const PROVIDER_LABELS = {
  school: 'School',
  guardian: 'Parent or carer',
};

/** The reader-facing label for a stored type id. */
export function documentTypeLabel(documentType) {
  const key = String(documentType || '').trim();
  return DOCUMENT_TYPES[key] || key || 'Document';
}

/** 'School' or 'Parent or carer'; the provider is always shown (FR5). */
export function providerLabel(providedBy) {
  const key = String(providedBy || '').trim().toLowerCase();
  return PROVIDER_LABELS[key] || key || 'Unknown';
}

/**
 * How many rows the overview card shows before it sends the reader to the
 * Documents page (FR1). The card is a prompt, not the filing cabinet.
 */
export const OVERVIEW_CARD_LIMIT = 5;

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];

/**
 * The date or year a document covers, as stored ('YYYY', 'YYYY-MM' or
 * 'YYYY-MM-DD'), read the way it was given: "2026", "March 2026",
 * "12 March 2026". Built from the parts, never through Date, so a year is
 * never shown as 1 January and no time zone moves the day. Null when there
 * is nothing readable.
 */
export function formatDateCovered(value) {
  const m = String(value || '').trim().match(/^(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?$/);
  if (!m) return null;
  const [, year, month, day] = m;
  if (!month) return year;
  const monthName = MONTHS[Number(month) - 1];
  if (!monthName) return null;
  if (!day) return `${monthName} ${year}`;
  return `${Number(day)} ${monthName} ${year}`;
}

/**
 * Whether the referral has been sent (Ready for triage), after which no
 * document can be added or removed. Mirrors is_referral_sent in
 * python-functions/constants/rms_referral_uploads.py.
 */
export function isReferralSent(patientRecord) {
  const readiness = patientRecord?.triageReadiness || {};
  return readiness.status === 'ready' || Boolean(readiness.markedAt);
}

/** The panel and card wording (TDI-602 Design Brief, sections 1 and 2). */
export const UPLOADS_EMPTY_SENT = 'No documents were added to this referral.';
export const UPLOADS_EMPTY_BEFORE_SEND = 'Documents can be added until the referral is sent.';
export const UPLOADS_PREPARING_PDF = 'Preparing PDF';
export const UPLOADS_VIEW_ALL = 'View all on Documents';

// A Word upload whose PDF copy failed used to stay "Preparing PDF" for good
// with no way to retry and no error (TDI-762): the fetch converts again on
// request, but nothing ever called it for a row without a copy. After this
// grace period the row offers "Try again", which calls the fetch; a copy that
// still fails tells staff plainly instead of waiting for ever.
export const UPLOADS_RETRY_AFTER_MS = 2 * 60 * 1000;
export const UPLOADS_TRY_AGAIN = 'Try again';
export const UPLOADS_PDF_FAILED =
  "We couldn't prepare this document. Ask the family or school to upload it as a PDF.";

/** Whether a not-ready row has waited long enough to offer "Try again":
 * conversion normally lands within moments of the upload, so a row still
 * unready after the grace period is stuck, not in progress. A row with no
 * uploadedAt cannot be aged, so it may retry. */
export function canRetryPdf(row, now = Date.now()) {
  if (!row || row.pdfReady !== false) return false;
  const uploaded = row.uploadedAt ? Date.parse(row.uploadedAt) : NaN;
  if (Number.isNaN(uploaded)) return true;
  return now - uploaded >= UPLOADS_RETRY_AFTER_MS;
}

/** A date as the rows read it: "12 March 2026", or null when there is none. */
export function formatDocumentDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}
